import React, { useEffect, useState } from "react";
import { apiClient, formatApiErrorDetail } from "../../lib/api";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../ui/dialog";
import { Clock, Loader2, CheckCircle2, XCircle, Users, Building2, Truck, Package, CalendarClock } from "lucide-react";
import { toast } from "sonner";

const OPTIONS = [
  { v: "todo", label: "Pending", Icon: Clock, tone: "bg-slate-100 text-slate-700 hover:bg-slate-200", active: "bg-slate-700 text-white" },
  { v: "in_progress", label: "Ongoing", Icon: Loader2, tone: "bg-amber-50 text-amber-700 hover:bg-amber-100", active: "bg-amber-500 text-white" },
  { v: "completed", label: "Completed", Icon: CheckCircle2, tone: "bg-emerald-50 text-emerald-700 hover:bg-emerald-100", active: "bg-emerald-600 text-white" },
  { v: "cancelled", label: "Cancelled", Icon: XCircle, tone: "bg-red-50 text-red-700 hover:bg-red-100", active: "bg-red-600 text-white" },
];

/** Opened when the Admin taps a sticky note (Phase 5 #2) — full task detail:
 * assigned employee, client/vendor, complete item list + item status. */
export function AdminTaskDetailSheet({ taskId, open, onOpenChange, onUpdated }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open || !taskId) { setData(null); return; }
    setLoading(true);
    apiClient.get(`/tasks/${taskId}/detail`).then((r) => setData(r.data)).catch(() => setData(null)).finally(() => setLoading(false));
  }, [open, taskId]);

  const setStatus = async (status) => {
    if (!data) return;
    setBusy(true);
    try {
      const { data: updated } = await apiClient.put(`/tasks/${taskId}`, { status });
      toast.success(`Marked ${OPTIONS.find((o) => o.v === status)?.label || status}`);
      onUpdated && onUpdated(updated);
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed");
    } finally {
      setBusy(false);
    }
  };

  const task = data?.task;
  const order = data?.order;
  const lines = (order?.lines && order.lines.length ? order.lines : order?.product ? [{ product: order.product, quantity: order.quantity, unit: order.unit }] : []) || [];
  const current = (task?.status || "todo").toLowerCase();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm rounded-3xl max-h-[85vh] overflow-y-auto" data-testid="admin-task-detail-sheet">
        <DialogHeader>
          <DialogTitle className="text-base">Task Details</DialogTitle>
          <DialogDescription className="text-xs">Assigned work item and current status</DialogDescription>
        </DialogHeader>
        {loading ? (
          <div className="py-8 text-center text-sm text-slate-400">Loading...</div>
        ) : !task ? (
          <div className="py-8 text-center text-sm text-slate-400">Task not found</div>
        ) : (
          <div className="space-y-3">
            <div className="grid grid-cols-1 gap-2 bg-slate-50 rounded-xl p-3" data-testid="admin-task-meta">
              <MetaRow Icon={Users} label="Employee" value={task.employee_name || "Unassigned"} testid="admin-task-employee" />
              {order?.client_name && <MetaRow Icon={Building2} label="Client" value={order.client_name} testid="admin-task-client" />}
              {order?.vendor_name && <MetaRow Icon={Truck} label="Vendor" value={order.vendor_name} testid="admin-task-vendor" />}
              {(order?.expected_delivery_date || order?.expected_delivery_time) && (
                <MetaRow
                  Icon={CalendarClock}
                  label="Delivery"
                  value={[order.expected_delivery_date, order.expected_delivery_time].filter(Boolean).join(" · ")}
                  testid="admin-task-delivery"
                />
              )}
            </div>

            {lines.length > 0 && (
              <div className="space-y-1.5" data-testid="admin-task-items">
                <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400 flex items-center gap-1">
                  <Package className="w-3 h-3" /> Items ({lines.length})
                </div>
                <div className="rounded-xl border border-slate-100 divide-y divide-slate-100 max-h-40 overflow-y-auto">
                  {lines.map((ln, i) => {
                    const done = !!(task.checklist || {})[String(i)];
                    return (
                      <div key={i} className="flex items-center justify-between px-3 py-2 text-sm" data-testid={`admin-task-item-${i}`}>
                        <span className="truncate text-slate-700">{ln.product} {ln.quantity ? `× ${ln.quantity}${ln.unit ? ` ${ln.unit}` : ""}` : ""}</span>
                        <span className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded shrink-0 ml-2 ${done ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>
                          {done ? "Prepared" : "Pending"}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {task.employee_note && (
              <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-3 text-xs text-indigo-900" data-testid="admin-task-note">
                <span className="font-bold uppercase tracking-wider text-[10px] text-indigo-600 block mb-1">Note from employee</span>
                {task.employee_note}
              </div>
            )}

            <div className="grid grid-cols-2 gap-2 pt-1">
              {OPTIONS.map((o) => {
                const on = current === o.v;
                return (
                  <button
                    key={o.v}
                    onClick={() => setStatus(o.v)}
                    disabled={busy}
                    data-testid={`admin-task-status-${o.v}`}
                    data-current={on ? "true" : "false"}
                    className={`h-12 rounded-2xl text-sm font-bold transition flex items-center justify-center gap-1.5 disabled:opacity-50 ${on ? o.active : o.tone}`}
                  >
                    <o.Icon className={`w-4 h-4 ${o.v === "in_progress" && (on || busy) ? "animate-spin" : ""}`} strokeWidth={2.4} />
                    {o.label}
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function MetaRow({ Icon, label, value, testid }) {
  return (
    <div className="flex items-center gap-2" data-testid={testid}>
      <Icon className="w-3.5 h-3.5 text-slate-400 shrink-0" />
      <span className="text-[11px] uppercase tracking-wider text-slate-400 font-semibold">{label}:</span>
      <span className="text-sm font-semibold text-slate-800 truncate">{value}</span>
    </div>
  );
}
